// Google reCAPTCHA v3 client utility.
//
// Loads the reCAPTCHA v3 script lazily (only when first needed) and exposes
// `getRecaptchaToken(action)` for any code path that calls the backend.
//
// Notes:
//  - SITE_KEY comes from REACT_APP_RECAPTCHA_SITE_KEY (CRA env convention).
//  - We mirror NEXT_PUBLIC_RECAPTCHA_SITE_KEY → REACT_APP_RECAPTCHA_SITE_KEY in .env.
//  - When the bypass flag is set OR the site key is missing, we return null and
//    the backend's own RECAPTCHA_BYPASS_IN_DEV is the source of truth.
//  - No npm dependency — minimal hand-rolled script injection.

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
const BYPASS =
    String(process.env.REACT_APP_RECAPTCHA_BYPASS_IN_DEV || "").toLowerCase() === "true";

let loadPromise = null;

function loadRecaptchaScript() {
    if (loadPromise) return loadPromise;
    loadPromise = new Promise((resolve, reject) => {
        if (!SITE_KEY) {
            // No site key in this build — silently no-op; backend will enforce.
            resolve();
            return;
        }
        const existing = document.querySelector(
            'script[data-recaptcha-v3="true"]'
        );
        if (existing) {
            if (existing.dataset.loaded === "true") {
                resolve();
            } else {
                existing.addEventListener("load", () => resolve());
                existing.addEventListener("error", () =>
                    reject(new Error("recaptcha-script-load-failed"))
                );
            }
            return;
        }
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
        script.async = true;
        script.defer = true;
        script.dataset.recaptchaV3 = "true";
        script.onload = () => {
            script.dataset.loaded = "true";
            resolve();
        };
        script.onerror = () => reject(new Error("recaptcha-script-load-failed"));
        document.body.appendChild(script);
    });
    return loadPromise;
}

/**
 * Get a reCAPTCHA v3 token for a given action.
 *
 * Returns:
 *   - null when bypass is enabled, when the site key is missing, or when the
 *     reCAPTCHA script can't load (privacy extension / offline). Callers should
 *     forward null gracefully — the backend will decide whether to allow.
 *   - The token string otherwise.
 */
export async function getRecaptchaToken(action) {
    if (BYPASS) return null;
    if (!SITE_KEY) return null;
    try {
        await loadRecaptchaScript();
        if (!window.grecaptcha) return null;
        return await new Promise((resolve, reject) => {
            window.grecaptcha.ready(() => {
                window.grecaptcha
                    .execute(SITE_KEY, { action })
                    .then(resolve)
                    .catch(reject);
            });
        });
    } catch {
        // Loading or execution failed. Return null — the request still goes
        // and the backend will reject in production (with calm copy) or
        // bypass in dev.
        return null;
    }
}

/**
 * Build a headers object containing the X-Recaptcha-Token header (if a token
 * was issued). Convenience helper for axios/fetch calls.
 */
export async function recaptchaHeaders(action) {
    const token = await getRecaptchaToken(action);
    return token ? { "X-Recaptcha-Token": token } : {};
}
